/**
 * MapLibre 直接加载 MapTiler style 时，URL 上的 language 参数不会改写各图层 text-field，
 * basic-v2 默认路名等仍为 coalesce(name:en, name)。需在 map load 后手动切换标注语言。
 */

import { MAPTILER_KEY, MAP_REGION_CN } from "@/utils/config";
import { isInChina } from "@/utils/commonUtils";
import { getMapTilerStyleLanguage } from "@/views/home/maptiler/useMapTilerSite";

export const MAP_LABEL_LANGUAGE = {
  ZH: "zh",
  EN: "en",
};

export const CHINESE_IDEOGRAPH_FONT =
  "'PingFang SC', 'Noto Sans SC', 'Microsoft YaHei', sans-serif";

/** 国内中文标注：优先简体 tag，并将台湾相关称谓统一为「台湾」 */
const CHINESE_TEXT_FIELD = [
  "let",
  "label",
  [
    "coalesce",
    ["get", "name:zh-Hans"],
    ["get", "name:zh"],
    ["get", "name"],
    ["get", "name:en"],
  ],
  [
    "match",
    ["var", "label"],
    "中华民国",
    "台湾",
    "中華民國",
    "台湾",
    "中華民國（臺灣）",
    "台湾",
    ["var", "label"],
  ],
];

const ENGLISH_TEXT_FIELD = [
  "coalesce",
  ["get", "name:en"],
  ["get", "name"],
];

export const getMapTilerStyleLanguageCode = (language = MAP_LABEL_LANGUAGE.ZH) =>
  language === MAP_LABEL_LANGUAGE.ZH ? "zh-Hans" : "en";

/** 国内站固定中文；境外站在中国 Tab 时也用中文（无 mapLabelLanguage 时的兜底） */
export const shouldUseChineseMapLabels = (mapRegion = MAP_REGION_CN) => {
  if (isInChina()) return true;
  return getMapTilerStyleLanguage(mapRegion) === "zh";
};

export const getMapLabelLanguage = (mapRegion = MAP_REGION_CN) =>
  shouldUseChineseMapLabels(mapRegion)
    ? MAP_LABEL_LANGUAGE.ZH
    : MAP_LABEL_LANGUAGE.EN;

/** MapTiler 底图样式：basic 简洁；streets 道路丰富；hybrid 卫星+道路标注，适合村庄 */
export const MAPTILER_STYLE = {
  BASIC: "basic-v2",
  STREETS: "streets-v2",
  HYBRID: "hybrid",
  SATELLITE: "satellite",
};

export const getMapTilerStyleUrl = (
  mapRegion = MAP_REGION_CN,
  styleId = MAPTILER_STYLE.BASIC,
  mapLabelLanguage
) => {
  const labelLanguage = mapLabelLanguage || getMapLabelLanguage(mapRegion);
  return `https://api.maptiler.com/maps/${styleId}/style.json?key=${MAPTILER_KEY}&language=${getMapTilerStyleLanguageCode(labelLanguage)}`;
};

export const getMapTilerBasicStyleUrl = (mapRegion = MAP_REGION_CN) =>
  getMapTilerStyleUrl(mapRegion, MAPTILER_STYLE.BASIC);

/** 门店地图：streets 底图，建筑轮廓/体块更丰富 */
export const getMapTilerStoresStyleUrl = (
  mapRegion = MAP_REGION_CN,
  mapLabelLanguage
) => getMapTilerStyleUrl(mapRegion, MAPTILER_STYLE.STREETS, mapLabelLanguage);

export function applyMapTilerLabelsLanguage(map, language = MAP_LABEL_LANGUAGE.ZH) {
  if (!map?.getStyle) return;

  const textField =
    language === MAP_LABEL_LANGUAGE.ZH ? CHINESE_TEXT_FIELD : ENGLISH_TEXT_FIELD;

  (map.getStyle().layers || []).forEach((layer) => {
    if (layer.type !== "symbol") return;
    const current = layer.layout?.["text-field"];
    if (current == null || current === "") return;

    try {
      map.setLayoutProperty(layer.id, "text-field", textField);
    } catch (e) {
      // 部分图层可能不可写，忽略即可
    }
  });
}

export function applyMapLabelsForRegion(map, mapRegion = MAP_REGION_CN) {
  applyMapTilerLabelsLanguage(map, getMapLabelLanguage(mapRegion));
}

/**
 * streets 底图：提高建筑物轮廓/体块可见度
 */
export function enhanceStandardMapBuildings(map) {
  if (!map?.getStyle) return;

  (map.getStyle().layers || []).forEach((layer) => {
    const id = String(layer.id || "");
    const type = layer.type;

    const isBuildingFootprint =
      type === "fill" && /^Building$/i.test(id);
    const isBuildingExtrusion =
      type === "fill-extrusion" && /building/i.test(id);

    if (isBuildingFootprint) {
      try {
        map.setLayerZoomRange(id, 11, 24);
        map.setPaintProperty(id, "fill-opacity", 0.62);
        map.setPaintProperty(id, "fill-color", "hsl(30, 8%, 72%)");
        map.setPaintProperty(id, "fill-outline-color", "hsl(35, 10%, 58%)");
      } catch (e) {
        try {
          map.setLayerZoomRange(id, 11, 24);
          map.setPaintProperty(id, "fill-opacity", 0.62);
        } catch (e2) {
          // ignore
        }
      }
    }

    if (isBuildingExtrusion) {
      try {
        map.setLayerZoomRange(id, 13, 24);
        map.setPaintProperty(id, "fill-extrusion-opacity", 0.78);
        map.setPaintProperty(id, "fill-extrusion-color", "hsl(44, 16%, 76%)");
      } catch (e) {
        // ignore
      }
    }
  });
}

/** 门店首页地图：按 mapLabelLanguage（?lang=）或 mapRegion 切换标注 + 建筑增强 */
export function applyHomeMapStyleEnhancements(
  map,
  { mapRegion = MAP_REGION_CN, mapLabelLanguage } = {}
) {
  const language =
    mapLabelLanguage ||
    (shouldUseChineseMapLabels(mapRegion)
      ? MAP_LABEL_LANGUAGE.ZH
      : MAP_LABEL_LANGUAGE.EN);
  applyMapTilerLabelsLanguage(map, language);
  enhanceStandardMapBuildings(map);
}

export const shouldUseChineseIdeographFont = (mapLabelLanguage) =>
  mapLabelLanguage !== MAP_LABEL_LANGUAGE.EN;
